<template>
  <transition name="force-password-fade">
    <div v-if="visible" class="force-password-overlay">
      <div class="force-password-card" role="dialog" aria-modal="true">
        <div class="card-icon">
          <v-icon size="28">mdi-lock-reset</v-icon>
        </div>
        <h2 class="card-title">Actualiza tu contraseña</h2>
        <p class="card-description">
          Hola <strong>{{ displayName }}</strong>, por seguridad debes definir una nueva contraseña
          antes de continuar usando la plataforma.
        </p>

        <div class="inputs">
          <SprintlyInput
            v-model="currentPassword"
            label="Contraseña actual"
            placeholder="Ingresa la contraseña temporal"
            type="password"
            prepend-icon="mdi-lock"
            required
          />

          <SprintlyInput
            v-model="newPassword"
            label="Nueva contraseña"
            placeholder="Mínimo 8 caracteres"
            type="password"
            prepend-icon="mdi-lock-check"
            required
          />

          <SprintlyInput
            v-model="confirmPassword"
            label="Confirmar contraseña"
            placeholder="Repite la nueva contraseña"
            type="password"
            prepend-icon="mdi-lock-check"
            required
          />
        </div>

        <ul class="password-hints">
          <li :class="{ ok: newPassword.length >= 8 }">Mínimo 8 caracteres</li>
          <li :class="{ ok: hasNumber }">Incluye al menos un número</li>
          <li :class="{ ok: passwordsMatch }">Coincide con la confirmación</li>
        </ul>

        <div v-if="error" class="alert error">
          <v-icon size="18">mdi-alert-circle</v-icon>
          <span>{{ error }}</span>
        </div>

        <div v-if="successMessage" class="alert success">
          <v-icon size="18">mdi-check-circle</v-icon>
          <span>{{ successMessage }}</span>
        </div>

        <div class="actions">
          <SprintlyButton
            variant="primary"
            :loading="loading"
            :disabled="!isFormValid || loading"
            block
            @click="handleSubmit"
          >
            Guardar nueva contraseña
          </SprintlyButton>
          <SprintlyButton
            variant="ghost"
            :disabled="loading"
            block
            @click="handleLogout"
          >
            Cerrar sesión
          </SprintlyButton>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import SprintlyInput from '@/components/SprintlyInput.vue';
import SprintlyButton from '@/components/SprintlyButton.vue';
import userService from '@/services/user.service';
import authService from '@/services/auth.service';
import { clearMustChangePasswordFlags } from '@/utils/passwordFlags';

@Component({
  components: {
    SprintlyInput,
    SprintlyButton,
  },
})
export default class ForcePasswordModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible!: boolean;

  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  loading = false;
  error: string | null = null;
  successMessage: string | null = null;

  get user() {
    return this.$store.state.auth.user;
  }

  get displayName(): string {
    if (!this.user) return 'usuario';
    if (this.user.firstName || this.user.lastName) {
      return `${this.user.firstName || ''} ${this.user.lastName || ''}`.trim();
    }
    if (this.user.fullName) {
      return this.user.fullName;
    }
    return this.user.username || 'usuario';
  }

  get hasNumber(): boolean {
    return /\d/.test(this.newPassword);
  }

  get passwordsMatch(): boolean {
    return this.newPassword.length > 0 && this.newPassword === this.confirmPassword;
  }

  get isFormValid(): boolean {
    return (
      this.currentPassword.length > 0 &&
      this.newPassword.length >= 8 &&
      this.hasNumber &&
      this.passwordsMatch
    );
  }

  @Watch('visible')
  onVisibleChange(value: boolean) {
    if (value) {
      this.lockScroll();
      this.resetForm();
    } else {
      this.unlockScroll();
    }
  }

  resetForm() {
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.loading = false;
    this.error = null;
    this.successMessage = null;
  }

  async handleSubmit() {
    if (!this.isFormValid || this.loading) {
      this.error = 'Completa todos los campos con una contraseña válida.';
      return;
    }

    this.loading = true;
    this.error = null;
    this.successMessage = null;

    try {
      await userService.changeMyPassword(this.currentPassword, this.newPassword);
      const updatedUser = await userService.getMyProfile();
      const normalizedUser = clearMustChangePasswordFlags(updatedUser);

      authService.updateStoredUser(normalizedUser);
      this.$store.commit('auth/SET_USER', normalizedUser);

      this.successMessage = '¡Contraseña actualizada! Ya puedes continuar.';
      this.$emit('updated');
      this.$toast?.success('Contraseña actualizada correctamente');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error al actualizar la contraseña';
      this.error = message;
    } finally {
      this.loading = false;
    }
  }

  async handleLogout() {
    await this.$store.dispatch('auth/logout');
  }

  lockScroll() {
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.body.style.overflow = '';
  }

  beforeDestroy() {
    this.unlockScroll();
  }
}
</script>

<style lang="scss" scoped>
.force-password-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.theme--light .force-password-overlay {
  background: rgba(0, 0, 0, 0.6);
}

.force-password-card {
  width: 100%;
  max-width: 520px;
  background: #070d16;
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 25px 55px rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.05);
  text-align: center;
}

.theme--light .force-password-card {
  background: #ffffff;
  border-color: #e0e0e0;
  box-shadow: 0 25px 45px rgba(0, 0, 0, 0.15);
}

.card-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(188, 60, 68, 0.15);
  border: 1px solid rgba(188, 60, 68, 0.3);
  color: #bc3c44;
}

.card-title {
  font-size: 1.4rem;
  margin-bottom: 8px;
  color: #ffffff;
}

.theme--light .card-title {
  color: #1a1a1a;
}

.card-description {
  color: #a0a4b8;
  margin-bottom: 24px;
  font-size: 0.95rem;
}

.theme--light .card-description {
  color: #4b4f63;
}

.inputs {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.password-hints {
  list-style: none;
  padding: 0;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.85rem;
  color: #6c7285;
  text-align: left;
}

.password-hints li.ok {
  color: #52c41a;
}

.alert {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.9rem;
  margin-top: 8px;
  text-align: left;
}

.alert.error {
  background: rgba(230, 126, 78, 0.15);
  color: #ffb199;
  border: 1px solid rgba(230, 126, 78, 0.4);
}

.alert.success {
  background: rgba(82, 196, 26, 0.15);
  color: #9be88b;
  border: 1px solid rgba(82, 196, 26, 0.4);
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}

.force-password-fade-enter-active,
.force-password-fade-leave-active {
  transition: opacity 0.25s ease;
}

.force-password-fade-enter,
.force-password-fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .force-password-card {
    padding: 28px 20px;
  }
}
</style>
